import { Toggle } from '@base-ui/react/toggle';
import { useRender } from '@base-ui/react/use-render';
import type { ComponentProps } from 'react';
import { cn } from './cn';

export type ChipTone = 'default' | 'onearly';

// Full class strings per tone (never built dynamically).
const TONE: Record<ChipTone, string> = {
  default:
    'border-border-highlight bg-control-unselected text-on-action-secondary data-[pressed]:border-control-selected data-[pressed]:bg-control-selected data-[pressed]:text-on-control-selected',
  // DESIGN.md › chip-onearly-selected: the "Powered by OnEarly" sign-in only.
  onearly:
    'border-action-onearly bg-control-unselected text-action-onearly data-[pressed]:border-action-onearly data-[pressed]:bg-action-onearly data-[pressed]:text-on-action-onearly',
};

type ChipProps =
  | (ComponentProps<typeof Toggle> & { variant?: 'toggle'; tone?: ChipTone })
  | (useRender.ComponentProps<'button'> & { variant: 'assist'; tone?: ChipTone });

/** One DS chip: selectable toggles by default, or context actions with native link semantics. */
export function Chip(props: ChipProps) {
  if (props.variant === 'assist') return <AssistAction {...props} />;
  const { variant: _variant, tone = 'default', className, ...toggleProps } = props;
  return (
    <Toggle
      className={cn(
        'inline-flex h-9 cursor-pointer items-center justify-center gap-internal-sm rounded-full border px-internal-lg font-brand text-label-sm transition-colors',
        TONE[tone],
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-highlight',
        className,
      )}
      {...toggleProps}
    />
  );
}

function AssistAction({
  variant: _variant,
  tone = 'default',
  className,
  render,
  ...props
}: Extract<ChipProps, { variant: 'assist' }>) {
  return useRender({
    defaultTagName: 'button',
    render,
    props: {
      ...(render ? {} : { type: 'button' }),
      ...props,
      className: cn(
        'relative inline-flex max-w-full cursor-pointer items-center justify-center gap-internal-sm rounded-xxl border px-internal-lg py-internal-md text-center font-brand text-label-sm transition-colors before:absolute before:inset-x-0 before:top-1/2 before:h-full before:min-h-tap-target before:-translate-y-1/2',
        TONE[tone],
        'hover:bg-action-secondary-focus active:bg-action-secondary-active focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-highlight',
        className,
      ),
    },
  });
}
